import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Star, Eye, ShoppingBag } from "lucide-react";
import { useI18n } from "../../i18n";
import { useWishlist } from "../../contexts/WishlistContext";
import { useCart } from "../../contexts/CartContext";
import { toast } from "sonner";
import QuickView from "./QuickView";

const ProductCard = ({ product, span = 1 }) => {
  const { lang, t } = useI18n();
  const { toggle, has } = useWishlist();
  const { add } = useCart();
  const [qvOpen, setQvOpen] = useState(false);
  const name = lang === "nl" ? product.name_nl : product.name_en;
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const lowStock = product.stock > 0 && product.stock < 5;

  return (
    <>
      <div data-testid={`product-card-${product.slug}`} className={`group flex flex-col ${span === 2 ? "md:col-span-2" : ""}`}>
        <Link to={`/product/${product.slug}`} className="block">
          <div className="editorial-image relative bg-secondary-warm aspect-[4/5] overflow-hidden">
            <img src={product.images?.[0]} alt={name} className="w-full h-full object-cover" loading="lazy" />
            {/* Badges */}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              {onSale && <span className="bg-terracotta text-white font-label px-2 py-1">Sale</span>}
              {product.new_arrival && !onSale && <span className="bg-ink text-white font-label px-2 py-1">New</span>}
              {lowStock && <span className="bg-white text-ink font-label px-2 py-1 border border-ink">{lang === "nl" ? "Bijna op" : "Almost gone"}</span>}
            </div>
            {/* Wishlist */}
            <button
              data-testid={`wishlist-toggle-${product.slug}`}
              onClick={(e) => { e.preventDefault(); toggle(product); toast.success(has(product.product_id) ? (lang==="nl"?"Verwijderd":"Removed") : (lang==="nl"?"Toegevoegd":"Added")); }}
              className="absolute top-3 right-3 w-9 h-9 bg-white/85 backdrop-blur flex items-center justify-center hover:bg-white transition-all"
            >
              <Heart className={`w-4 h-4 ${has(product.product_id) ? "fill-terracotta text-terracotta" : "text-ink"}`} />
            </button>
            {/* Quick view */}
            <button
              data-testid={`quickview-trigger-${product.slug}`}
              onClick={(e) => { e.preventDefault(); setQvOpen(true); }}
              className="absolute bottom-3 right-3 w-10 h-10 bg-white text-ink flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0 hover:bg-ink hover:text-white"
              aria-label="Quick view"
            >
              <Eye className="w-4 h-4" />
            </button>
            {/* Quick add */}
            <button
              data-testid={`quick-add-${product.slug}`}
              disabled={product.stock === 0}
              onClick={(e) => { e.preventDefault(); add(product); toast.success(lang === "nl" ? "Toegevoegd" : "Added to cart"); }}
              className="absolute bottom-3 left-3 right-16 bg-ink text-white text-xs font-label py-3 opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0 hover:bg-terracotta flex items-center justify-center gap-2 disabled:opacity-30"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> {product.stock === 0 ? t("out_of_stock") : t("add_to_cart")}
            </button>
          </div>
        </Link>
        <div className="pt-4 pb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to={`/product/${product.slug}`} className="font-display text-base md:text-lg block leading-tight truncate hover:text-terracotta transition-colors">{name}</Link>
            {product.seller_slug && product.seller_slug !== "snapshop-official" && (
              <Link to={`/store/${product.seller_slug}`} data-testid={`sold-by-${product.slug}`} onClick={(e) => e.stopPropagation()} className="text-[10px] font-label text-ink-muted hover:text-terracotta block mt-1 truncate">
                {lang === "nl" ? "Verkocht door" : "Sold by"} <span className="text-ink">{product.seller_name}</span>
              </Link>
            )}
            <div className="flex items-center gap-2 mt-1.5">
              {product.rating_count > 0 ? (
                <div className="flex items-center gap-1 text-xs text-ink-muted">
                  <Star className="w-3 h-3 fill-terracotta text-terracotta" />
                  <span>{product.rating_avg?.toFixed(1)}</span>
                  <span className="text-ink-muted/60">({product.rating_count})</span>
                </div>
              ) : (
                <div className="text-xs text-ink-muted font-label">{product.sku}</div>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            {onSale && <div className="text-xs text-ink-muted line-through">€{product.compare_at_price?.toFixed(2)}</div>}
            <div className="font-display text-lg">€{product.price.toFixed(2)}</div>
          </div>
        </div>
      </div>
      <QuickView product={product} open={qvOpen} onOpenChange={setQvOpen} />
    </>
  );
};

export default ProductCard;
